import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { Maximize2, Minimize2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Badge, PageHeader, Select, Tabs } from "../components/ui";
import Overview from "./dashboard/Overview";
import Capacity from "./dashboard/Capacity";
import Live from "./dashboard/Live";

const RANGES = ["1h", "6h", "24h", "7d", "30d"];

export default function Dashboard() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const range = sp.get("range") || "24h";
  const target = sp.get("target") || "";
  const fullscreen = sp.get("fullscreen") === "1";
  const [ov, setOv] = useState(null);
  const [health, setHealth] = useState(null);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [at, setAt] = useState(null);
  const set = (patch) => { const n = new URLSearchParams(sp); Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); setSp(n, { replace: true }); };
  const load = useCallback(async () => {
    setLoading(true);
    try { const [o, h] = await Promise.all([api(`/dashboard/overview?range=${range}&target=${target}`), api(`/dashboard/health?range=${range}`)]); setOv(o); setHealth(h); setAt(new Date()); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [range, target]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api("/clusters").then((c) => setTargets(Array.isArray(c) ? c : [])).catch(() => {}); }, []);
  useEffect(() => { if (!fullscreen) return; const h = (e) => e.key === "Escape" && set({ fullscreen: "" }); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [fullscreen]);
  useEffect(() => { if (!fullscreen) return; const t = setInterval(load, 10000); return () => clearInterval(t); }, [fullscreen, load]);
  const live = ov?.counts?.models_live || 0;
  const status = ov ? `${live} model${live === 1 ? "" : "s"} live · ${ov.counts.endpoints_live}/${ov.counts.endpoints_total} endpoints · ${ov.traffic.idle ? "no traffic in range" : `${ov.traffic.requests.toLocaleString()} requests in ${range}`}` : "loading…";
  if (fullscreen) return (
    <div className="fixed inset-0 z-40 bg-cx-bg overflow-y-auto p-6 lg:p-10" data-testid="dashboard-fullscreen">
      <div className="flex items-center justify-between mb-6"><div><div className="text-2xl font-semibold">CoreX · Live</div><div className="text-sm text-cx-muted" data-testid="dashboard-status-line">{status}</div></div><button className="cx-btn cx-btn-secondary" onClick={() => set({ fullscreen: "" })} data-testid="dashboard-exit-fullscreen"><Minimize2 size={14} /> Exit (Esc)</button></div>
      <Live ov={ov} health={health} fullscreen />
    </div>);
  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle={<span data-testid="dashboard-status-line">{status}</span>} help="dashboard-and-capacity" badge={health && <Badge color={{ healthy: "green", at_risk: "yellow", critical: "red", idle: "gray" }[health.label]}>{health.label.replace("_", " ")}</Badge>}
        actions={<div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1" data-testid="range-chips">{RANGES.map((r) => <button key={r} className={clsx("cx-chip", range === r && "cx-chip-active")} onClick={() => set({ range: r })} data-testid={`range-${r}`}>{r}</button>)}</div>
          <Select value={target} onChange={(e) => set({ target: e.target.value })} className="!w-auto !py-1 !text-xs" data-testid="target-filter"><option value="">All targets</option>{targets.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
          <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={load} data-testid="dashboard-refresh" title={at ? `updated ${at.toLocaleTimeString()}` : ""}><RefreshCw size={13} className={loading ? "animate-spin" : ""} /></button>
          <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => set({ fullscreen: "1", tab: "live" })} data-testid="dashboard-fullscreen-btn" title="Fullscreen wallboard"><Maximize2 size={13} /></button>
        </div>} />
      <Tabs tabs={[{ value: "overview", label: "Overview" }, { value: "capacity", label: "Capacity" }, { value: "live", label: "Live" }]} value={tab} onChange={(v) => set({ tab: v })} />
      <div className="mt-6">
        {tab === "overview" && <Overview ov={ov} health={health} range={range} />}
        {tab === "capacity" && <Capacity ov={ov} range={range} />}
        {tab === "live" && <Live ov={ov} health={health} />}
      </div>
    </div>);
}
